import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell, Legend, Tooltip } from 'recharts';
import { ScenarioDataItem } from '../pages/Ecosystem';
import { Empty } from 'antd';

interface ScenarioBarChartProps {
  data?: ScenarioDataItem[];
  title?: string;
}

// Custom legend component
const CustomLegend: React.FC<{ payload?: { value: string; color: string }[] }> = ({ payload }) => {
  if (!payload) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '12px',
        marginTop: '16px',
        padding: '8px',
      }}
    >
      {payload.map((entry, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
          <div
            style={{
              width: '12px',
              height: '12px',
              backgroundColor: entry.color,
              marginRight: '6px',
              borderRadius: '2px',
            }}
          />
          Bundle {entry.value}
        </div>
      ))}
    </div>
  );
};

// Custom tooltip component
const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        fontSize: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#333' }}>Bundle {label}</p>
      {payload.map((entry: any, idx: number) => (
        <p key={idx} style={{ margin: '2px 0', color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
        </p>
      ))}
    </div>
  );
};

const ScenarioBarChart: React.FC<ScenarioBarChartProps> = ({ data, title }) => {
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          border: '1px solid #f0f0f0',
          borderRadius: '8px',
          backgroundColor: '#fafafa',
          padding: '20px',
          margin: '20px 0',
        }}
      >
        <Empty description='No Graph data for scenario!' />
      </div>
    );
  }

  // Get metric keys (exclude Bundles and Color)
  const metricKeys = Object.keys(data[0]).filter((k) => k !== 'Bundles' && k !== 'Color');
  return (
    <div
      style={{
        padding: '20px',
        border: '1px solid #f0f0f0',
        borderRadius: '8px',
        backgroundColor: 'rgb(199, 240, 210)',
        margin: '20px 0',
      }}
    >
      <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>{title}</h2>

      <ResponsiveContainer width='100%' height={400}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} barCategoryGap='20%'>
          <CartesianGrid strokeDasharray='3 3' stroke='#e0e0e0' />
          <XAxis dataKey='Bundles' tick={{ fontSize: 12 }} axisLine={{ stroke: '#666' }} />
          <YAxis tick={{ fontSize: 12 }} axisLine={{ stroke: '#666' }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend verticalAlign='bottom' align='center' height={60} content={<CustomLegend />} />

          {metricKeys.map((metric) => (
            <Bar key={metric} dataKey={metric} name={metric}>
              {data.map((entry, idx) => (
                <Cell key={`cell-${metric}-${idx}`} fill={entry.Color || '#888888'} /> // Fallback color
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScenarioBarChart;
